import Mosaic from 'mosaic-framework';

import '../components/navigation-bar';
import '../components/footer';
import '../components/round-button';
import '../components/code-segment';

import '../styles/docs.less';


export default new Mosaic({
    name: 'actions-page',
    created() {
        window.scrollTo({ top: 0 });

        const menubtn = document.getElementById('menu-button');
        menubtn.style.display = 'block';

        if(menubtn.onclick) return;

        menubtn.onclick = () => {
            const menu = document.getElementById('doc-menu');
            menu.style.transform = 'translateX(0%)';
        }
    },
    view() {
        return html`<div class='doc-page'>
            <navigation-bar></navigation-bar>
            
            <h1 class='section-title' tabindex='0'>🥊 Actions and User-Defined Properties</h1>
            <p class='section-text' tabindex='0'>
                In addition to the lifecycle functions, Mosaics can have custom, user-defined
                <b>actions</b> on them. These actions/functions are ultimately what will make 
                your components and your web apps, in general, function. To add an action all 
                you need to do is add it to your constructor options:
            </p>
            <span class='section-text'><i style='color:gray;margin-bottom:0px'>index.js</i></span>
            <code-segment type='JS'>
                new Mosaic({
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;name: 'my-component',
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;customAction: function() {
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;
                &nbsp;&nbsp;&nbsp;&nbsp;
                console.log("This is my custom action!");
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;
                }
                <br>
                });
            </code-segment>

            <p class='section-text' tabindex='0'>
                As mentioned a few sections back, Mosaic options allow for any user-defined
                property to be added. It is important to remember, though, that Mosaics are
                still extensions of HTMLElements, so any custom properties that you add will
                simply override the previous value. Be sure to choose unique names for your
                custom properties so as not to tamper with the underlying HTMLElement. Let's
                take a look at a bad example that will unintentionally override a component's
                event listener:
            </p>
            <b style='color:crimson'>
                <ion-icon name="close"></ion-icon>
                &nbsp;Bad: "onclick" should be a function, and should not be overriden.
            </b>
            <code-segment type='JS'>
                new Mosaic({
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;name: 'my-component',
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;onclick: true
                <br>
                });
            </code-segment>
            <p class='section-text' tabindex='0'>
                To combat an issue you like this, you should instead add an event listener
                in the <b>created</b> lifecycle function.
            </p>
            <p class='section-text' tabindex='0'>
                This advice goes for all kinds of user-defined properties (further
                abbreviated to UDPs). For example, you could add a UDP called "primaryColor"
                which will be used throughout your component. As long as there is no previous
                definition for primaryColor on your component or in the HTMLElement class,
                you can simply reference it using the following syntax:
            </p>
            <code-segment type='JS'>
                console.log(this.customPropertyName);
                <br>
                this.customAction();
            </code-segment>
            <br>
            
            <h1 class='section-title' tabindex='0'>Using an "Actions" Object</h1>
            <p class='section-text' tabindex='0'>
                Sometimes your component can become cluttered if you add too many UDPs. Say,
                for example, you have a lot of actions that your component needs to handle
                and you add them all directly to the constructor options. If you want to
                organize them better, you can simply replace them with another UDP of your
                choosing and group them into an object. In this example, we will take all of
                our actions and group them into a single object called "actions":
            </p>
            <code-segment type='JS'>
                new Mosaic({
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;name: 'my-component',
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;actions: {
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;
                &nbsp;&nbsp;&nbsp;&nbsp;
                customAction1() {
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;
                &nbsp;&nbsp;&nbsp;&nbsp;
                &nbsp;&nbsp;&nbsp;&nbsp;
                console.log("First action!");
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;
                &nbsp;&nbsp;&nbsp;&nbsp;
                },
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;
                &nbsp;&nbsp;&nbsp;&nbsp;
                customAction2() {
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;
                &nbsp;&nbsp;&nbsp;&nbsp;
                &nbsp;&nbsp;&nbsp;&nbsp;
                console.log("Second action!");
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;
                &nbsp;&nbsp;&nbsp;&nbsp;
                },
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;
                &nbsp;&nbsp;&nbsp;&nbsp;
                customAction3() {
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;
                &nbsp;&nbsp;&nbsp;&nbsp;
                &nbsp;&nbsp;&nbsp;&nbsp;
                console.log("Third action!");
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;
                &nbsp;&nbsp;&nbsp;&nbsp;
                },
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;
                }
                <br>
                });
            </code-segment>
            <p class='section-text' tabindex='0'>
                Much cleaner, right? And now, depending on your text editor, you can simply
                minimize the entire "actions" object if you do not current need to look at it.
            </p>

            <h1 class='section-title' tabindex='0'>Event Handlers</h1>
            <p class='section-text' tabindex='0'>
                Although we will dive more into component views in the next section,
                we will quickly discuss how to add events to your components. Of course,
                you can use the lifecycle functions to add and remove event listeners, but
                you can also add an event directly to your component by using the "dollar
                sign bracket" syntax and referencing your function like so:
            </p>
            <code-segment type='JS'>
                &lt;button onclick='\${this.myFunctionName}'&gt;&lt;/button&gt;
            </code-segment>
            <p class='section-text' tabindex='0'>
                Depending on your function definition as well as the scope of the call, you
                may or may not need to bind it to the current component instance by adding
                ".bind(this)" before the end the brackets.
            </p>

            <h1 class='section-title' tabindex='0'>Demo</h1>
            <p class='section-text' tabindex='0'>
                This demo quickly shows a UDP for an action being added to a component. when
                you click the button, the action will be triggered.
            </p>
            <iframe width="100%" height="300" src="//jsfiddle.net/authman2/gfk8L95t/18/embedded/js,html,result/" allowfullscreen="allowfullscreen" allowpaymentrequest frameborder="0"></iframe>
            <br>

            <p class='section-text' tabindex='0' style='text-align:center;'>
                In the next section, we will finally take a look at the view function
                and how you can use it to write out what your components will look like!
            </p>
            <div style="text-align: center;">
                <round-button onclick='${() => this.router.send('/data')}'>
                    <ion-icon name="arrow-round-back"></ion-icon>
                    Observable Data
                </round-button>
                &nbsp;&nbsp;
                <round-button onclick='${() => this.router.send('/view')}'>
                    View Function
                    <ion-icon name="arrow-round-forward"></ion-icon>
                </round-button>
            </div>

            <app-footer></app-footer>
        </div>`
    }
});